import { useCallback, useState } from 'react'

/**
 * A small UI preference — the filter you last chose, the page size you prefer — kept in
 * `localStorage` so it survives a reload.
 *
 * Only for conveniences that can be wrong without hurting: the value is per-browser, is
 * never read back by the server, and can vanish whenever someone clears site data.
 *
 * Every read is validated, because a stored value outlives the code that wrote it: a
 * filter we have since renamed, or a page size we stopped offering, would otherwise come
 * back and put the UI in a state it can no longer produce. Storage can also be absent or
 * throw outright (private windows, blocked site data), so every path falls back to the
 * default rather than failing.
 */
export default function useStoredPreference<T>(
    key: string,
    fallback: T,
    isValid: (value: unknown) => value is T
): [T, (next: T) => void] {
    const [value, setValue] = useState<T>(() => {
        try {
            const raw = window.localStorage.getItem(key)
            if (raw === null) return fallback
            const parsed: unknown = JSON.parse(raw)
            return isValid(parsed) ? parsed : fallback
        } catch {
            return fallback
        }
    })

    const store = useCallback(
        (next: T) => {
            setValue(next)
            try {
                window.localStorage.setItem(key, JSON.stringify(next))
            } catch {
                // Unavailable or full: the preference simply doesn't outlive this page
                // load, which is the same as never having set it.
            }
        },
        [key]
    )

    return [value, store]
}
